<template>
  <!-- Manage Board Modal -->
  <BaseModal @close="$emit('close')" cardWidth="490px" noPadding noToolbarPadding noExit>
    <template #toolbar>
      <div class="flex items-center gap-3 pt-1 pr-5 pb-1">
        <img src="../../assets/images/manage-board-logo.png" alt="" style="width: 46px; height: 58px" />
        <div class="flex flex-col" style="padding-bottom: 14px">
          <BaseText type="label" size="md" class="text-text-loud">
            {{ board.id ? "Edit Board" : "Create Board" }}
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal">
            Boards hold your saved ads
          </BaseText>
        </div>
      </div>
      <button class="absolute top-3 right-3 p-1.5" @click="$emit('close')">
        <TagRemoveIcon stroke="#5E6678" />
      </button>
    </template>
    <template #default>
      <div class="w-full flex flex-col p-3 border-t border-border-normal">
        <div class="w-full flex items-center justify-between">
          <BaseText type="label" size="sm" class="w-full text-text-muted">
            Board Name
          </BaseText>
          <BaseText v-if="getBoardNameErrors" type="body" size="xs" class="text-primary-red-100 min-w-max">
            {{ getBoardNameErrors }}
          </BaseText>
        </div>
        <div data-tour="board-name" class="w-full flex flex-col">
          <input v-model="currentBoard.name" class="form-input w-full px-2 py-1.5 mt-1 rounded-md"
          placeholder="client_nike" @input="boardNameInputChanged" @keypress.enter.prevent="handleSaveBoard"/>
          <!-- One-click name suggestions (guided setup flow) -->
          <div v-if="suggestedNames.length && !board.id" class="w-full flex items-center gap-1.5 flex-wrap mt-2">
            <BaseText type="body" size="xs" class="text-text-subdued mr-0.5">
              Try:
            </BaseText>
            <button v-for="suggestion in suggestedNames" :key="`suggestion-${suggestion}`" type="button"
            class="px-2 py-1 rounded-md border transition-colors duration-100"
            :class="isSuggestionSelected(suggestion)
              ? 'bg-primary-blue-10 border-primary-blue-25 text-primary-blue-100'
              : 'bg-white border-border-normal text-text-muted hover:bg-neutral-25'"
            @click="applySuggestedName(suggestion)">
              <BaseText type="label" size="xs">
                {{ suggestion }}
              </BaseText>
            </button>
          </div>
        </div>
        <div class="w-full flex items-center gap-1 mt-3">
          <BaseText type="label" size="sm" class="text-text-muted">
            Description
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-subdued">
            (Optional)
          </BaseText>
        </div>
        <input v-model="currentBoard.description" class="form-input w-full px-2 py-1.5 mt-1 rounded-md"
        placeholder="A brief description of your board" @keypress.enter.prevent="handleSaveBoard" />
        <div v-if="!board.id" data-tour="board-folder" class="w-full flex flex-col">
          <div class="w-full flex items-center gap-1 mt-3 mb-1">
            <BaseText type="label" size="sm" class="text-text-muted">
              Location
            </BaseText>
          </div>
          <!-- Folder Select Dropdown -->
          <BaseSingleDropdown
            use-parent-width
            enable-search
            search-placeholder="Search Folders"
            :options="folderOptions"
            :selected-obj="formattedSelectedFolder"
            @change="selectedFolder = $event"
          />
        </div>
        <!-- Bulk download section -->
        <div v-if="board.id" class="w-full flex flex-col p-3 mt-3 rounded-md bg-neutral-25">
          <BaseText type="label" size="sm" class="w-full text-text-muted">
            Bulk Download
          </BaseText>
          <BaseText type="body" size="sm" class="w-full text-text-subdued mt-1">
            Download this board as a .ZIP folder (videos and images only)
          </BaseText>
          <div class="w-full flex items-center mt-3 gap-1.5">
            <!-- Download board button -->
            <button class="download-board-btn group flex items-center gap-1.5 px-2.5 py-1.5 rounded-md"
            :class="{'downloading cursor-default': loadingDownload}" :disabled="loadingDownload" type="button"
            @click.prevent="initializeBoardExport">
              <BaseLoadingSpinnerCircle v-if="loadingDownload" class="text-text-subdued" :duration="1" />
              <DownloadIcon v-else class="text-icon-normal transition-colors group-hover:text-icon-muted" />
              <BaseText type="label" size="sm" class="transition-colors" :class="loadingDownload ? 'text-text-subdued' : 'text-text-muted'">
                {{ loadingDownload ? "Downloading" : "Download" }} Board
              </BaseText>
            </button>
            <!-- Download progress indicator -->
            <div v-if="loadingDownload" class="flex-grow flex flex-col gap-1 px-3">
              <BaseText type="body" size="xs" class="text-text-disabled">
                {{ downloadProgressText }}
              </BaseText>
              <div class="w-full h-1.5 p-px rounded-full bg-neutral-50">
                <div ref="progress-bar" class="h-full w-0 rounded-full bg-neutral-300" style="transition: width 100ms ease-in-out" />
              </div>
            </div>
          </div>
          <!-- Fallback direct download info text -->
          <div v-if="directDownloadInfo" class="direct-download-info w-full flex items-end" style="height: 96px">
            <div class="w-full flex gap-1.5 items-start bg-neutral-50 px-1.5 py-2.5 rounded-lg">
              <InfoIcon class="text-icon-muted flex-shrink-0" />
              <div class="flex-grow flex flex-col gap-2">
                <BaseText type="body" size="sm" class="text-text-muted">
                  Your download is large ({{ directDownloadInfo.size }}) and may take a while to complete. We've started a direct download.
                </BaseText>
                <div class="w-full flex items-center gap-1">
                  <BaseText type="body" size="xs" class="text-text-normal">
                    If your download didn't start automatically,
                  </BaseText>
                  <a :href="directDownloadInfo.url" :download="directDownloadInfo.filename" target="_blank" @click.stop>
                    <BaseText type="body" size="xs" class="text-text-muted hover:underline cursor-pointer">
                      click here
                    </BaseText>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Update/Create button -->
      <div class="flex items-center justify-between p-5 border-t border-border-normal">
        <button class="px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
        @click.prevent="$emit('close')">
          <BaseText type="label" size="sm" class="text-text-muted">
            Cancel
          </BaseText>
        </button>
        <div data-tour="board-create">
          <BaseButton primary :loading="loadingSave" :disabled="loadingSave"
          @click="handleSaveBoard">
            {{ board.id ? "Update Board" : "Create Board" }}
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'

// Icons
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import FolderIcon from '../globals/Icons/SpyderV2Icons/FolderIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'
import DownloadIcon from '../globals/Icons/DownloadIcon.vue'
import InfoIcon from '../globals/Icons/InfoSimpleIcon.vue'

let isFramePending = false // Prevents render update buffering for the download progress bar

export default {
  name: 'ManageBoardModal',
  mixins: [clickaway],
  components: {
    TagRemoveIcon,
    FolderIcon,
    ChevronIcon,
    DownloadIcon,
    InfoIcon
  },
  props: {
    // Board
    board: {
      type: Object,
      default: () => ({})
    },
    initialFolder: {
      type: Object,
      default: () => ({ id: 'default', name: 'Default Folder' })
    },
    // Optional one-click board names shown under the name input (create only)
    suggestedNames: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      currentBoard: {},
      selectedFolder: this.initialFolder,
      loadingSave: false,

      // Board download states
      loadingDownload: false,
      downloadProgressText: '',
      downloadPollInterval: null,
      directDownloadInfo: null,
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['isFreeTier', 'getTeam']),
    ...mapGetters('BoardsModule', ['getBoards', 'getFolders']),
    folderOptions () {
      const folders = this.getFolders.filter(folder => folder.id !== this.selectedFolder.id)
      if (this.selectedFolder.id && this.selectedFolder.id !== 'default') {
        folders.push({ id: null, name: 'Default Folder' })
      }
      return folders.sort((a, b) => a.name.localeCompare(b.name)) // sort alphabetically
    },
    // Get Board Name Errors
    getBoardNameErrors () {
      if (!this.$v.currentBoard.$anyDirty) return null
      if (!this.$v.currentBoard.name.required) return 'Name is required'
      return null
    },
    formattedSelectedFolder () {
      const {id, name} = this.selectedFolder
      return {
        ...this.selectedFolder,
        name: id === 'starred' ? 'Default Folder (Starred)' : name
      }
    },
  },
  validations: {
    currentBoard: {
      name: {
        required
      }
    }
  },
  mounted () {
    this.currentBoard = { ...this.board }
    if (this.currentBoard.name) this.currentBoard.name = this.currentBoard.name.substring(1)
    if (!this.board.id) {
      this.$nextTick(() => {
        this.handleDropdownScroll()
        this.$refs['folder-list'].addEventListener('scroll', this.handleDropdownScroll)
      })
    }
  },
  beforeDestroy () {
    if (!this.board.id) this.$refs['folder-list'].removeEventListener('scroll', this.handleDropdownScroll)
    if (this.downloadPollInterval) clearInterval(this.downloadPollInterval)
  },
  methods: {
    ...mapActions('BoardsModule', ['fetchBoards', 'fetchFolders']),

    // ================================================================================
    // ================================= DATA METHODS =================================
    // ================================================================================

    // Handle Save Board
    async handleSaveBoard () {
      this.$v.$reset()
      if (this.$v.$invalid) {
        this.$v.$touch()
        return
      }

      this.loadingSave = true
      try {
        const payload = {
          ads: this.currentBoard.ads || [],
          description: this.currentBoard.description || '',
          name: `#${this.currentBoard.name
            .replace(/ /g, '_')
            .toLowerCase()
            .trim()}`,
        }
        if (this.getTeam) payload.teamId = this.getTeam.id

        const boardFolder = (this.selectedFolder?.id && !['default', 'starred'].includes(this.selectedFolder.id))
          ? this.selectedFolder
          : null

        let newBoardId = null
        if (!this.currentBoard.id) {
          newBoardId = await FirebaseAPI.Boards.create(payload, boardFolder)
          this.$showAlert({
            message: 'Board created successfully',
            type: 'success'
          })
        } else {
          await FirebaseAPI.Boards.update(this.currentBoard.id, payload)
          this.$showAlert({
            message: 'Board updated successfully',
            type: 'success'
          })
          this.$emit('save', this.currentBoard.id)
        }

        await this.fetchBoards()
        await this.fetchFolders()

        if (newBoardId && this.selectedFolder.id === 'starred') {
          this.$emit('star-board', newBoardId)
        }
        if (newBoardId) {
          this.$emit('created', newBoardId, payload.name)
        }

        this.$emit('close')
      } catch (e) {
        console.log(e)
        this.$showAlert({
          message: `Error ${this.currentBoard.id ? 'updating' : 'creating'} board`,
          type: 'error'
        })
      } finally {
        this.loadingSave = false
      }
    },

    // ============================ BOARD DOWNLOAD METHODS ============================

    async initializeBoardExport () {
      if (!this.board.id || this.loadingDownload) return

      this.downloadProgressText = 'Requesting board export... (1/3)'
      this.loadingDownload = true

      // Initialize the board export job
      try {
        const {data, status} = await ForeplayAPI.Ads.createBoardExportJob(this.board.id)
        if (status !== 200 && status !== 202) throw new Error(data.message)
      } catch (error) {
        console.error('Error initializing board export:', error)
        this.$showAlert({
          message: error,
          type: 'error'
        })
        this.loadingDownload = false
        this.downloadProgressText = ''
        return
      }

      // Define the job status request function
      const getJobStatus = async () => {
        const {data, status} = await ForeplayAPI.Ads.getBoardExportStatus(this.board.id)
        if (status !== 200) throw new Error(data.message)

        // If the job is finished, return the download URL
        if (data.status === 1 && data.url) {
          return {
            url: data.url,
            progress: 100
          }
        }
        // Otherwise, return the job progress
        return { progress: data.progress }
      }

      // Poll the job status every 1.5 seconds
      const timeout = Date.now() + 300000 // 5 minutes
      try {
        let serverIsProcessing = false
        const executePoll = async () => { 
          const pollResult = await getJobStatus()
          if (pollResult.url) {
            // Once the export job gives a download URL, clear the polling interval and download the zip
            clearInterval(this.downloadPollInterval)
            this.executeBoardDownload(pollResult.url)
            return false // Prevent polling from initiating
          }
          else if (pollResult.progress > 0) {
            // Progress will start incrementing when the job moves from pending to processing
            if (!serverIsProcessing) {
              this.downloadProgressText = 'Server processing board... (2/3)'
              serverIsProcessing = true
            }
            const progress = Math.round(pollResult.progress / 2)
            this.updateDownloadProgressBar(progress)
            return true
          }
          else {
            // If the job is still pending, check if the timeout has been reached
            if (Date.now() > timeout) throw new Error('Export request timed out')
            return true
          }
        }

        // Check the job status immediately and start polling if necessary
        if (await executePoll()) {
          this.downloadPollInterval = setInterval(executePoll, 1500)
        }
      } catch (error) {
        clearInterval(this.downloadPollInterval)
        console.error('Error downloading board:', error)
        this.$showAlert({
          message: error,
          type: 'error'
        })
        this.loadingDownload = false
        this.downloadProgressText = ''
      }
    },
    async executeBoardDownload(url) {
      this.downloadProgressText = 'Downloading board contents... (3/3)'
      // Fetch the asset
      const response = await nativeFetch(url, { mode: 'cors' })
      if (!response.ok) {
        throw new Error('Failed to fetch board export')
      }

      // Get the content length to track download progress
      const contentLength = response.headers.get('Content-Length')
      if (!response.body || !contentLength) {
        throw new Error('Readable steams or content length not supported')
      }

      // Total bytes of the .zip file
      const totalBytes = parseInt(contentLength, 10)

      // Determine the max file size we allow to be handled by the site (otherwise, use direct download)
      const deviceDRAM = navigator?.deviceMemory || window?.deviceMemory || 4 // Default to 4GB if not available
      const maxBlobBytes = (deviceDRAM * 1024 * 1024 * 1024) * 0.2 // 20% of the device's RAM
      
      const filename = `${this.board.name.substring(1).replace(/ /g, '_').toLowerCase()}-export.zip`
      let downloadUrl
      if (totalBytes < maxBlobBytes) {
        // If the file is small enough, we can enable in-browser streaming with progress tracking
        // Create a readable stream
        let loadedBytes = 0
        const reader = response.body.getReader()
        const stream = new ReadableStream({
          start: async (controller) => {
            while (true) {
              const { done, value } = await reader.read()
              if (done) break

              loadedBytes += value.byteLength
              this.updateDownloadProgressBar(50 + Math.round((loadedBytes / totalBytes) * 50))

              controller.enqueue(value)
            }
            controller.close()
          }
        })

        // Create a new blob from the stream
        const blob = await new Response(stream).blob()
        downloadUrl = URL.createObjectURL(blob)
      } else {
        // If the file is too large, we need to download it directly
        downloadUrl = url
        this.directDownloadInfo = {
          size: this.formatFileSize(totalBytes),
          url: url,
          filename: filename
        }
      }

      this.loadingDownload = false
      this.downloadProgressText = ''
      
      // Create a temporary download link and simulate click to trigger direct download
      const anchor = document.createElement('a')
      anchor.href = downloadUrl
      anchor.download = filename
      anchor.addEventListener('click', (event) => { event.stopPropagation() })
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      // Revoke the URL to free up memory
      URL.revokeObjectURL(url)
    },

    // ================================================================================
    // ================================== UI METHODS ==================================
    // ================================================================================

    updateDownloadProgressBar (progress) {
      if (isFramePending) return
      isFramePending = true
      requestAnimationFrame(() => {
        this.$refs['progress-bar'].style.width = `${progress}%`
        isFramePending = false
      })
    },
    handleDropdownScroll () {
      console.log('scrolling')
      const scrollableElm = this.$refs['folder-list']
      this.isMaxScroll = Math.ceil(scrollableElm.scrollHeight - (scrollableElm.scrollTop)) <= (scrollableElm.clientHeight + 2) // 2px buffer
    },
    boardNameInputChanged (event) {
      const name = `${event.target.value.replace(/ /g, '_').toLowerCase()}`
      this.currentBoard.name = name
    },
    normalizeSuggestion (suggestion) {
      return suggestion.replace(/ /g, '_').toLowerCase()
    },
    isSuggestionSelected (suggestion) {
      return this.currentBoard.name === this.normalizeSuggestion(suggestion)
    },
    applySuggestedName (suggestion) {
      this.currentBoard.name = this.normalizeSuggestion(suggestion)
      this.$v.currentBoard.name.$touch()
    },
    formatFileSize (bytes) {
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      const index = Math.floor(Math.log(bytes) / Math.log(1024))
      return (bytes / Math.pow(1024, index)).toFixed(2) + units[index]
    }
  }
}
</script>

<style scoped>
.form-input {
  border: none;
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 150ms ease-in-out;

  /* Body/Small */
  font-feature-settings: 'ss01' on, 'cv10' on, 'liga' off, 'calt' off;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.form-input:focus {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.10), 0px 0px 0px 1px rgba(0, 56, 108, 0.10);
}
.form-input::placeholder {
  color: #808899;
  opacity: 0.8;
  transition: opacity 150ms ease-in-out;
}
.form-input:focus::placeholder {
  opacity: 0.5;
}
.download-board-btn {
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: background-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
.download-board-btn:hover, .download-board-btn.downloading {
  background-color: #ECEFF3; /* bg-neutral-50 */
  box-shadow: none;
}

.direct-download-info {
  animation: directDownloadInfoAppear 500ms ease-in-out forwards;
}
@keyframes directDownloadInfoAppear {
  0% {
    opacity: 0;
    height: 0px;
  }
  50% {
    opacity: 0;
    height: 96px;
  }
  100% {
    opacity: 1;
    height: 96px;
  }
}
</style>
